import api from './api';
import { Driver, DriverFormData, DriverStats } from '../types/driver';
import { mapBackendToFrontendStatus, mapFrontendToBackendStatus, BackendStatus } from '../utils/statusMapping';

// Helper function to convert camelCase to snake_case
const toSnakeCase = (obj: any): any => {
  if (obj === null || typeof obj !== 'object') return obj;
  
  if (Array.isArray(obj)) {
    return obj.map(item => toSnakeCase(item));
  }
  
  return Object.keys(obj).reduce((acc: any, key) => {
    const snakeKey = key.replace(/([A-Z])/g, '_$1').toLowerCase();
    acc[snakeKey] = toSnakeCase(obj[key]);
    return acc;
  }, {});
};

// Helper function to convert snake_case to camelCase
const toCamelCase = (obj: any): any => {
  if (obj === null || typeof obj !== 'object') return obj;
  
  if (Array.isArray(obj)) {
    return obj.map(item => toCamelCase(item));
  }
  
  return Object.keys(obj).reduce((acc: any, key) => {
    const camelKey = key.replace(/(_\w)/g, (k) => k[1].toUpperCase());
    acc[camelKey] = toCamelCase(obj[key]);
    return acc;
  }, {});
};

export const getDrivers = async (): Promise<Driver[]> => {
  try {
    const response = await api.get('/drivers');
    // Convert snake_case to camelCase and map status
    return (response.data || []).map((driver: any) => {
      const mappedDriver = toCamelCase(driver);
      if (driver.status) {
        mappedDriver.status = mapBackendToFrontendStatus(driver.status as BackendStatus);
      }
      return mappedDriver;
    });
  } catch (error) {
    console.error('Error fetching drivers:', error);
    return [];
  }
};

export const getDriver = async (id: string): Promise<Driver> => {
  const response = await api.get(`/drivers/${id}`);
  const driver = toCamelCase(response.data);
  // Map backend status to frontend status
  if (driver.status) {
    driver.status = mapBackendToFrontendStatus(driver.status as BackendStatus);
  }
  return driver;
};

export const createDriver = async (driverData: DriverFormData): Promise<Driver> => {
  try {
    // Map frontend status to backend status
    const dataToSend = {
      ...driverData,
      status: mapFrontendToBackendStatus(driverData.status as any)
    };
    
    // Convert camelCase to snake_case for the API
    const snakeData = toSnakeCase(dataToSend);
    const response = await api.post('/drivers', snakeData);
    
    // Convert snake_case to camelCase and map status for the frontend
    const result = toCamelCase(response.data);
    if (result.status) {
      result.status = mapBackendToFrontendStatus(result.status as BackendStatus);
    }
    return result;
  } catch (error: any) {
    if (error.response && error.response.status === 409) {
      throw new Error('A driver with this email already exists');
    }
    throw error;
  }
};

export const updateDriver = async (id: string, driverData: Partial<DriverFormData>): Promise<Driver> => {
  try {
    // Create a new object for the data to send
    const dataToSend: any = { ...driverData };
    
    // Map frontend status to backend status if status is being updated
    if (driverData.status) {
      dataToSend.status = mapFrontendToBackendStatus(driverData.status as any);
    }
    
    // Convert camelCase to snake_case for the API
    const snakeData = toSnakeCase(dataToSend);
    console.log('Updating driver with data:', { id, snakeData });
    
    const response = await api.put(`/drivers/${id}`, snakeData);
    
    // Convert snake_case to camelCase and map status for the frontend
    const result = toCamelCase(response.data);
    if (result.status) {
      result.status = mapBackendToFrontendStatus(result.status as BackendStatus);
    }
    
    console.log('Update successful:', result);
    return result;
  } catch (error: any) {
    console.error('Error updating driver:', error);
    if (error.response) {
      console.error('Response data:', error.response.data);
      console.error('Response status:', error.response.status);
      console.error('Response headers:', error.response.headers);
      
      // Convert error response keys to camelCase for consistent error handling
      if (error.response.data && typeof error.response.data === 'object') {
        error.response.data = toCamelCase(error.response.data);
      }
    }
    throw error;
  }
};

export const deleteDriver = async (id: string): Promise<void> => {
  await api.delete(`/drivers/${id}`);
};

export const updateDriverStatus = async (id: string, status: string, currentLocation?: string): Promise<Driver> => {
  try {
    const dataToSend: any = {
      status: mapFrontendToBackendStatus(status as any)
    };
    
    if (currentLocation) {
      dataToSend.current_location = currentLocation;
    }
    
    console.log('Updating driver status with data:', { id, dataToSend });
    
    const response = await api.put(`/drivers/${id}/status`, dataToSend);
    
    // Convert snake_case to camelCase and map status for the frontend
    const result = toCamelCase(response.data);
    if (result.status) {
      result.status = mapBackendToFrontendStatus(result.status as BackendStatus);
    }
    
    console.log('Status update successful:', result);
    return result;
  } catch (error: any) {
    console.error('Error updating driver status:', error);
    if (error.response) {
      console.error('Response data:', error.response.data);
      console.error('Response status:', error.response.status);
    }
    throw error;
  }
};

export const getDriverStats = async (): Promise<DriverStats> => {
  const response = await api.get('/drivers/stats');
  return response.data;
};
